import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Trash2, Save, Clock, AlertCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Matrix-style editor for attendance windows across ALL parks.
 *
 * One row per park, one column per "Slot N" (Slot 1, 2, 3, ...). The number
 * of slot columns is the MAX of windows.length across all parks so different
 * parks can have different numbers of slots. Empty cells show "—" with an
 * inline "+ slot" button. Two tabs switch between the PMC and Contractor
 * window sets — each persists separately on the server via PUT
 * `/admin/parks/{id}/attendance-windows`.
 */
export function AttendanceWindowsMatrix({ settings, onSaved }) {
  const [parks, setParks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState("pmc_supervisor");
  // dirty[role][parkId] = mutated window list
  const [dirty, setDirty] = useState({ pmc_supervisor: {}, contractor_supervisor: {} });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/parks");
      const sorted = [...data].sort((a, b) => {
        const ca = parseInt(a.code, 10), cb = parseInt(b.code, 10);
        if (!isNaN(ca) && !isNaN(cb)) return ca - cb;
        return String(a.code).localeCompare(String(b.code));
      });
      setParks(sorted);
      setDirty({ pmc_supervisor: {}, contractor_supervisor: {} });
    } catch {
      toast.error("Failed to load parks");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const field = role === "pmc_supervisor" ? "pmc_attendance_windows" : "contractor_attendance_windows";

  // get the effective window list for a given park & role (dirty > park > settings-default)
  const getWindows = (p) => {
    const did = dirty[role][p.id];
    if (did !== undefined) return did;
    const list = p[field] || [];
    if (list.length === 0) return (settings?.[field] || []).map((w) => ({ ...w, id: "" }));
    return list;
  };

  // max windows across all parks (caps the column count)
  const maxCols = useMemo(() => {
    let m = 0;
    for (const p of parks) {
      const w = getWindows(p);
      if (w.length > m) m = w.length;
    }
    return Math.max(m, 2); // always at least 2 visible columns for breathing room
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parks, dirty, role, settings]);

  const updateCell = (parkId, idx, patch) => {
    setDirty((d) => {
      const park = parks.find((p) => p.id === parkId);
      const current = d[role][parkId] !== undefined ? d[role][parkId] : getWindows(park);
      const next = current.map((w, i) => (i === idx ? { ...w, ...patch } : w));
      return { ...d, [role]: { ...d[role], [parkId]: next } };
    });
  };

  const addCell = (parkId, idx) => {
    setDirty((d) => {
      const park = parks.find((p) => p.id === parkId);
      const current = d[role][parkId] !== undefined ? d[role][parkId] : getWindows(park);
      // pad up to idx with placeholders if needed, then push a new slot at idx
      const padded = [...current];
      while (padded.length < idx) {
        padded.push({ id: "", label: `Slot ${padded.length + 1}`, start_time: "09:00", end_time: "11:00", active: true });
      }
      padded.splice(idx, 0, {
        id: "",
        label: `Slot ${padded.length + 1}`,
        start_time: park?.worker_start_time || "09:00",
        end_time: park?.worker_end_time || "11:00",
        active: true,
      });
      return { ...d, [role]: { ...d[role], [parkId]: padded } };
    });
  };

  const removeCell = (parkId, idx) => {
    setDirty((d) => {
      const park = parks.find((p) => p.id === parkId);
      const current = d[role][parkId] !== undefined ? d[role][parkId] : getWindows(park);
      const next = current.filter((_, i) => i !== idx);
      return { ...d, [role]: { ...d[role], [parkId]: next } };
    });
  };

  const saveAll = async () => {
    const changed = Object.entries(dirty[role]);
    if (changed.length === 0) return toast.message("No changes to save");
    setSaving(true);
    let ok = 0;
    let failed = 0;
    for (const [parkId, list] of changed) {
      // sanity check each window
      let bad = false;
      for (const w of list) {
        if (!w.label?.trim()) { bad = true; break; }
        if (!/^\d{2}:\d{2}$/.test(w.start_time) || !/^\d{2}:\d{2}$/.test(w.end_time)) { bad = true; break; }
        if (w.start_time >= w.end_time) { bad = true; break; }
      }
      if (bad) { failed += 1; continue; }
      try {
        await api.put(`/admin/parks/${parkId}/attendance-windows`, { role, windows: list });
        ok += 1;
      } catch {
        failed += 1;
      }
    }
    setSaving(false);
    if (failed === 0) toast.success(`Saved ${ok} park${ok === 1 ? "" : "s"}`);
    else toast.error(`${ok} saved, ${failed} failed (check times / labels)`);
    await load();
    onSaved?.();
  };

  const dirtyCount = Object.keys(dirty[role]).length;
  const inWorkerHours = (park, t) => {
    if (!park.worker_start_time || !park.worker_end_time) return true;
    return t >= park.worker_start_time && t <= park.worker_end_time;
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <Clock className="h-4 w-4 text-primary" />
        <div className="font-display text-base font-semibold">Attendance windows · per park matrix</div>
      </div>
      <p className="text-xs text-muted-foreground">
        Each row is a park, each column is a capture-slot. Edit times directly. Parks can have different numbers of slots — empty cells show a "+" to add a new slot. Outside these windows, supervisors must request your permission.
      </p>

      <Tabs value={role} onValueChange={setRole}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <TabsList>
            <TabsTrigger value="pmc_supervisor" data-testid="att-matrix-pmc-tab">ITCOT / PMC supervisors</TabsTrigger>
            <TabsTrigger value="contractor_supervisor" data-testid="att-matrix-con-tab">Contractor supervisors</TabsTrigger>
          </TabsList>
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-muted-foreground">
              {dirtyCount > 0
                ? <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 font-medium text-amber-900"><AlertCircle className="h-3 w-3" />{dirtyCount} unsaved</span>
                : <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 font-medium text-emerald-800"><CheckCircle2 className="h-3 w-3" />All saved</span>
              }
            </span>
            <Button size="sm" onClick={saveAll} disabled={saving || dirtyCount === 0} data-testid="att-matrix-save-all">
              <Save className="mr-1.5 h-4 w-4" />
              {saving ? "Saving…" : `Save changes`}
            </Button>
          </div>
        </div>

        {["pmc_supervisor", "contractor_supervisor"].map((r) => (
          <TabsContent key={r} value={r} className="mt-3">
            {loading ? (
              <div className="text-sm text-muted-foreground">Loading parks…</div>
            ) : parks.length === 0 ? (
              <div className="rounded-md border border-dashed p-4 text-center text-xs text-muted-foreground">No parks yet.</div>
            ) : (
              <div className="overflow-x-auto rounded-md border" data-testid={`att-matrix-${r}`}>
                <table className="w-full text-xs">
                  <thead className="bg-slate-100 text-[10px] uppercase tracking-wider text-slate-700">
                    <tr>
                      <th className="sticky left-0 z-10 whitespace-nowrap bg-slate-100 px-2 py-2 text-left">#</th>
                      <th className="sticky left-8 z-10 whitespace-nowrap bg-slate-100 px-2 py-2 text-left">Park</th>
                      <th className="whitespace-nowrap px-2 py-2 text-center">Worker hrs</th>
                      {Array(maxCols).fill(0).map((_, i) => (
                        <th key={i} className="whitespace-nowrap px-2 py-2 text-center">Slot {i + 1}</th>
                      ))}
                      <th className="whitespace-nowrap px-2 py-2 text-center">Add</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parks.map((p) => {
                      const windows = getWindows(p);
                      const isDirty = dirty[role][p.id] !== undefined;
                      return (
                        <tr key={p.id} className={`border-t ${isDirty ? "bg-amber-50/60" : "hover:bg-slate-50"}`} data-testid={`att-matrix-row-${p.code}`}>
                          <td className={`sticky left-0 z-10 whitespace-nowrap px-2 py-2 font-mono text-sm font-semibold ${isDirty ? "bg-amber-50" : "bg-white"}`}>{p.code}</td>
                          <td className={`sticky left-8 z-10 whitespace-nowrap px-2 py-2 ${isDirty ? "bg-amber-50" : "bg-white"}`}>
                            <div className="font-medium text-slate-900 text-sm">{p.name}</div>
                            <div className="text-[10px] text-muted-foreground" title="When a park has no slots of its own, the global default windows from app settings are used.">
                              {(p[field] || []).length === 0 ? "uses app default" : `${(p[field] || []).length} slot${(p[field] || []).length === 1 ? "" : "s"}`}
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-2 py-2 text-center text-[11px] font-mono text-slate-700">
                            {p.worker_start_time && p.worker_end_time
                              ? <span>{p.worker_start_time}–{p.worker_end_time}</span>
                              : <span className="text-slate-400">—</span>}
                          </td>
                          {Array(maxCols).fill(0).map((_, i) => {
                            const w = windows[i];
                            if (!w) {
                              return (
                                <td key={i} className="whitespace-nowrap px-2 py-2 text-center align-middle">
                                  <button
                                    type="button"
                                    onClick={() => addCell(p.id, i)}
                                    className="inline-flex items-center gap-0.5 rounded border border-dashed border-slate-300 px-2 py-1 text-[10px] text-slate-500 hover:bg-amber-50 hover:border-amber-400 hover:text-amber-700"
                                    data-testid={`att-matrix-cell-add-${p.code}-${i}`}
                                  >
                                    <Plus className="h-3 w-3" />
                                  </button>
                                </td>
                              );
                            }
                            const startBad = !inWorkerHours(p, w.start_time);
                            const endBad = !inWorkerHours(p, w.end_time);
                            return (
                              <td key={i} className="whitespace-nowrap px-1 py-1 align-middle">
                                <div className={`flex flex-col gap-0.5 rounded border ${(startBad || endBad) ? "border-amber-400 bg-amber-50/40" : "border-slate-200 bg-white"} p-1`}>
                                  <Input
                                    value={w.label}
                                    onChange={(e) => updateCell(p.id, i, { label: e.target.value })}
                                    className="h-6 text-[11px] px-1.5"
                                    placeholder="Label"
                                    data-testid={`att-matrix-cell-label-${p.code}-${i}`}
                                  />
                                  <div className="flex items-center gap-0.5">
                                    <Input
                                      type="time"
                                      value={w.start_time}
                                      onChange={(e) => updateCell(p.id, i, { start_time: e.target.value })}
                                      className="h-6 text-[11px] px-1 w-[88px]"
                                      data-testid={`att-matrix-cell-start-${p.code}-${i}`}
                                    />
                                    <span className="text-[10px] text-slate-400">–</span>
                                    <Input
                                      type="time"
                                      value={w.end_time}
                                      onChange={(e) => updateCell(p.id, i, { end_time: e.target.value })}
                                      className="h-6 text-[11px] px-1 w-[88px]"
                                      data-testid={`att-matrix-cell-end-${p.code}-${i}`}
                                    />
                                    <button
                                      type="button"
                                      onClick={() => removeCell(p.id, i)}
                                      className="ml-0.5 rounded text-rose-500 hover:bg-rose-50"
                                      title="Remove slot"
                                      data-testid={`att-matrix-cell-remove-${p.code}-${i}`}
                                    >
                                      <Trash2 className="h-3 w-3" />
                                    </button>
                                  </div>
                                </div>
                              </td>
                            );
                          })}
                          <td className="whitespace-nowrap px-2 py-2 text-center align-middle">
                            <button
                              type="button"
                              onClick={() => addCell(p.id, windows.length)}
                              className="inline-flex items-center gap-0.5 rounded border px-1.5 py-1 text-[10px] hover:bg-amber-50 hover:border-amber-400 hover:text-amber-700"
                              data-testid={`att-matrix-add-slot-${p.code}`}
                            >
                              <Plus className="h-3 w-3" />slot
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>

      <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
        <Clock className="h-3 w-3" />All times are IST. Rows with unsaved edits are tinted amber.
      </div>
    </div>
  );
}
